import React from "react";
import { motion } from "framer-motion";
import { ScrollText, Languages, FileSignature, ArrowUpRight, ArrowLeftRight } from "lucide-react";
import { waUrl } from "@/lib/site";

const services = [
  {
    icon: ScrollText,
    title: "Tradução Juramentada",
    desc: "Documentos com fé pública e validade legal em todo o Brasil.",
    items: [
      "Certidões (nascimento, casamento, óbito)",
      "Diplomas e históricos acadêmicos",
      "Contratos e procurações",
      "Antecedentes criminais",
      "Documentos empresariais",
      "Declarações e atestados",
      "Documentos oficiais",
    ],
  },
  {
    icon: Languages,
    title: "Tradução e Interpretação",
    desc: "Acompanhamento profissional em situações que exigem rigor linguístico.",
    items: [
      "Eventos empresariais e corporativos",
      "Reuniões jurídicas e audiências",
      "Contratos bilíngues",
      "Atendimento institucional",
      "Negociações Brasil / Hispanoamérica",
    ],
  },
  {
    icon: FileSignature,
    title: "Assinaturas e Validações",
    desc: "Soluções completas de autenticação digital e física.",
    items: [
      "Assinatura digital GOV.BR Ouro",
      "Assinatura física do tradutor",
      "Reconhecimento em cartório",
      "Entrega digital e impressa",
    ],
  },
];

const Services = () => {
  return (
    <section id="servicos" data-testid="services-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <p className="overline mb-5"><span className="gold-rule mr-3 align-middle" />Serviços</p>
          <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-tight">
            Soluções jurídicas em <span className="italic">tradução & interpretação</span>.
          </h2>
          <p className="mt-6 text-[#475569] text-base lg:text-lg leading-relaxed">
            Atendimento sob medida para pessoas físicas, empresas, escritórios de
            advocacia e instituições — com sigilo absoluto e prazos rigorosos.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                data-testid={`service-card-${i}`}
                className="bg-[#FAFAFA] border border-[#E2E8F0] hover:border-[#C5A059] transition-all duration-500 p-8 md:p-10 flex flex-col group"
              >
                <div className="flex items-start justify-between mb-8">
                  <Icon size={32} strokeWidth={1.25} className="text-[#C5A059]" />
                  <span className="font-serif-display text-xs tracking-[0.25em] text-[#475569]">
                    0{i + 1}
                  </span>
                </div>

                <h3 className="font-serif-display text-2xl text-[#0A1128] leading-tight mb-3">
                  {s.title}
                </h3>
                <p className="text-sm text-[#475569] leading-relaxed mb-6">{s.desc}</p>

                <ul className="space-y-2.5 text-sm text-[#0A1128] mb-8 flex-1">
                  {s.items.map((it, j) => (
                    <li key={j} className="flex gap-3 items-start">
                      <span className="mt-2 w-1 h-1 rounded-full bg-[#C5A059] flex-shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href={waUrl(`Olá, gostaria de informações sobre: ${s.title}.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`service-cta-${i}`}
                  className="inline-flex items-center gap-2 text-sm text-[#0A1128] group-hover:text-[#C5A059] transition-colors mt-auto border-t border-[#E2E8F0] pt-5"
                >
                  <span className="tracking-wide">Solicitar pelo WhatsApp</span>
                  <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                </a>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
